import { DeviceLabels, MeetingStatus, QUALITY_OPTIONS } from 'frontend/constants/chime';
import { VIDEO_QUALITY_TYPES } from 'frontend/constants';
import { action } from '@ember/object';
import { equal, reads } from 'macro-decorators';
import { findDeviceById } from 'frontend/utils/devices';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

export default class ChimeMeetingManagerService extends Service {
  @service('chime.event-manager') eventManager;
  @service('chime.sdk') sdkService;
  @reads('sdkService.sdk') chimeSdk;

  @tracked audioVideo;
  @tracked selectedAudioInputDevice;
  @tracked selectedAudioOutputDevice;
  @tracked selectedVideoInputDevice;
  @tracked meetingStatus;
  @tracked localAttendeeId;
  @tracked deviceLabels;
  @tracked audioInputDevices;
  @tracked audioOutputDevices;
  @tracked videoInputDevices;
  @tracked videoInput;

  #audioVideoCallbacks = [];
  meetingSession = null;

  @equal('meetingStatus', MeetingStatus.Succeeded) isConnected;
  @equal('meetingStatus', MeetingStatus.Loading) isLoading;
  @equal('meetingStatus', MeetingStatus.Left) isLeft;

  async initMeetingSession(configurationData, options) {
    const {
      MeetingSessionConfiguration,
      ConsoleLogger,
      LogLevel,
      DefaultDeviceController,
      DefaultMeetingSession,
      DefaultEventController,
    } = this.chimeSdk;
    const meetingSessionConfiguration = new MeetingSessionConfiguration(
      configurationData.meeting,
      configurationData.attendee
    );
    const logger = new ConsoleLogger('MeetingLogs', LogLevel.WARN);
    const deviceController = new DefaultDeviceController(logger);
    const eventController = new DefaultEventController(meetingSessionConfiguration, logger);
    this.meetingSession = new DefaultMeetingSession(
      meetingSessionConfiguration,
      logger,
      deviceController,
      eventController
    );

    this.eventManager.start(eventController);

    this.audioVideo = this.meetingSession.audioVideo;
    this.meetingSession.dataMessageHandler = {};
    this.#publishAudioVideo();
    this.setupAudioVideoObservers();

    const { deviceLabels, devices, videoQuality } = options;
    this.setupDeviceLabelTrigger(deviceLabels);
    await this.listAndSelectDevices(deviceLabels, devices, videoQuality);

    this.localAttendeeId = configurationData.attendee.attendeeId;
    this.meetingStatus = MeetingStatus.Loading;
  }

  @action
  audioVideoDidStart() {
    this.meetingStatus = MeetingStatus.Succeeded;

    if (!this.videoInput) return;
    this.audioVideo.startLocalVideoTile();
  }

  @action
  audioVideoDidStop() {
    this.meetingStatus = MeetingStatus.Left;
    this.audioVideo?.removeObserver(this.audioVideoObservers);
    this.leave();
  }

  setupAudioVideoObservers() {
    if (!this.audioVideo) return;

    this.audioVideoObservers = {
      audioVideoDidStart: this.audioVideoDidStart,
      audioVideoDidStop: this.audioVideoDidStop,
    };
    this.audioVideo.addObserver(this.audioVideoObservers);
  }

  async start(options) {
    return this.audioVideo?.start(options);
  }

  @action
  async leave() {
    if (!this.audioVideo) return;

    await this.stopVideoInput();
    this.selectedVideoInputDevice = undefined;
    this.audioVideo.unbindAudioElement();
    this.audioVideo.stop();
    this.audioVideo = null;
    this.audioElement = null;
    this.#publishAudioVideo();
  }

  async updateDeviceLists() {
    this.audioInputDevices = (await this.audioVideo?.listAudioInputDevices()) || [];
    this.videoInputDevices = (await this.audioVideo?.listVideoInputDevices()) || [];
    this.audioOutputDevices = (await this.audioVideo?.listAudioOutputDevices()) || [];
  }

  async listAndSelectDevices(
    deviceLabels = DeviceLabels.AudioAndVideo,
    selectedDevices,
    videoQuality
  ) {
    let isAudioDeviceRequested, isVideoDeviceRequested;

    await this.updateDeviceLists();

    switch (deviceLabels) {
      case DeviceLabels.None:
        break;
      case DeviceLabels.Audio:
        isAudioDeviceRequested = true;
        break;
      case DeviceLabels.Video:
        isVideoDeviceRequested = true;
        break;
      case DeviceLabels.AudioAndVideo:
        isAudioDeviceRequested = true;
        isVideoDeviceRequested = true;
        break;
    }

    await this.#startDevices({
      isAudioDeviceRequested,
      isVideoDeviceRequested,
      selectedDevices,
      videoQuality,
    });
  }

  setupDeviceLabelTrigger(deviceLabels = DeviceLabels.AudioAndVideo) {
    this.deviceLabels = deviceLabels;

    let callback;

    let constraints = {};

    switch (deviceLabels) {
      case DeviceLabels.Audio:
        constraints.audio = true;
        break;
      case DeviceLabels.Video:
        constraints.video = true;
        break;
      case DeviceLabels.AudioAndVideo:
        constraints.audio = true;
        constraints.video = true;
        break;
    }

    callback = async () => {
      let devices = await navigator.mediaDevices.enumerateDevices();
      let hasVideoInput = devices.some(value => value.kind === 'videoinput');

      return await navigator.mediaDevices.getUserMedia({
        audio: constraints.audio,
        video: constraints.video && hasVideoInput,
      });
    };

    this.audioVideo?.setDeviceLabelTrigger(callback);
  }

  async startVideoInputDevice() {
    try {
      this.videoInput = await this.audioVideo?.startVideoInput(this.selectedVideoInputDevice);

      if (this.isConnected) this.audioVideo?.startLocalVideoTile();
    } catch (err) {
      throw new Error('Failed to start video input device', {
        cause: err,
      });
    }
  }

  async stopVideoInput() {
    if (this.isConnected) this.audioVideo?.stopLocalVideoTile();

    await this.audioVideo?.stopVideoInput();
    this.audioVideo?.removeLocalVideoTile();
    this.videoInput = null;
  }

  subscribeToAudioVideo(callback) {
    this.#audioVideoCallbacks.push(callback);
    callback(this.audioVideo);
  }

  async handleAudioInputChange(deviceId, { startDevice }) {
    this.selectedAudioInputDevice = deviceId;

    if (!startDevice) return;

    await this.startAudioInput();
  }

  async handleAudioOutputChange(deviceId) {
    this.selectedAudioOutputDevice = deviceId;

    if (!new this.chimeSdk.DefaultBrowserBehavior().supportsSetSinkId()) return;

    await this.audioVideo.chooseAudioOutput(this.selectedAudioOutputDevice);

    if (!this.audioElement) {
      this.audioElement = document.createElement('audio');
      this.audioElement.style.display = 'none';
      this.audioVideo.bindAudioElement(this.audioElement);
    }
  }

  async startAudioInput() {
    this.audioVideo.startAudioInput(this.selectedAudioInputDevice);
  }

  selectVideoQuality(quality = VIDEO_QUALITY_TYPES.hd) {
    if (quality === VIDEO_QUALITY_TYPES.auto || !QUALITY_OPTIONS[quality]) {
      quality = VIDEO_QUALITY_TYPES.hd; // todo: process auto quality
    }
    let { width, height, frameRate, maxBandwidth } = QUALITY_OPTIONS[quality];

    this.audioVideo.chooseVideoInputQuality(width, height, frameRate);
    this.audioVideo.setVideoMaxBandwidthKbps(maxBandwidth);
  }

  async handleVideoInputChange(deviceId, { startDevice, videoQuality } = {}) {
    if (this.videoInput) {
      await this.stopVideoInput();
    }

    this.selectedVideoInputDevice = deviceId;

    this.selectVideoQuality(videoQuality);
    if (!startDevice) return;

    return this.startVideoInputDevice();
  }

  #publishAudioVideo() {
    this.#audioVideoCallbacks.forEach(callback => callback(this.audioVideo));
  }

  async #startDevices({
    isAudioDeviceRequested,
    isVideoDeviceRequested,
    videoQuality,
    selectedDevices,
  }) {
    let { selectedAudioInputDeviceId, selectedAudioOutputDeviceId, selectedVideoDeviceId } =
      selectedDevices;

    await Promise.all([
      this.handleAudioInputChange(
        findDeviceById(this.audioInputDevices, selectedAudioInputDeviceId),
        { startDevice: isAudioDeviceRequested }
      ),
      this.handleAudioOutputChange(
        findDeviceById(this.audioOutputDevices, selectedAudioOutputDeviceId)
      ),
      this.handleVideoInputChange(findDeviceById(this.videoInputDevices, selectedVideoDeviceId), {
        startDevice: isVideoDeviceRequested,
        videoQuality,
      }),
    ]);
  }
}
